import React from "react";
import { AiFillStar } from "@react-icons/all-files/ai/AiFillStar";
import { AiOutlineStar } from "@react-icons/all-files/ai/AiOutlineStar";
import { RiShareBoxFill as OpenIcon } from "@react-icons/all-files/ri/RiShareBoxFill";
import { classNames } from "repoV2/utils/common/render/classNames";
import { DEFAULT_PROFILE_PIC_SRC } from "repoV2/utils/common/assets/assets";
import { trimText } from "@Utils";
import Rating from "react-rating";
import { roundOff } from "@Utils/number";
import { ButtonV2 } from "@Modules/common/ButtonV2";
import commonStyles from "../commonStyles.module.scss";
import styles from "./review.module.scss";
import { IReviewsProps } from "./IReviews";
import { REVIEWS_LIMIT } from "./data";
import useReviews from "./useReviews";

const Reviews = (props: IReviewsProps): JSX.Element | null => {
    const { hideComponent, reviews, avgRating, handleShowReviews } =
        useReviews(props);

    if (hideComponent) return null;

    return (
        <div className={commonStyles.detailsSection}>
            <div className="d-flex justify-content-between align-items-center">
                <div className={commonStyles.detailsSectionTitle}>Reviews</div>
                {avgRating > 0 && (
                    <div
                        className={`d-flex align-items-start ${styles.rating_container}`}
                    >
                        <Rating
                            placeholderRating={avgRating}
                            readonly
                            emptySymbol={
                                <AiOutlineStar
                                    className={`${styles.emptyStarIcon} `}
                                />
                            }
                            placeholderSymbol={
                                <AiFillStar
                                    className={`${styles.filledStarIcon} `}
                                />
                            }
                        />
                        <span className={`ml-2 ${styles.ratingText}`}>
                            {roundOff(avgRating, 2)}/5
                        </span>
                    </div>
                )}
            </div>
            <div className={commonStyles.detailsSectionBody}>
                <div
                    className={classNames(
                        styles.reviewSection,
                        [1, 3].includes(reviews.length) && styles.oddLength
                    )}
                >
                    {reviews.slice(0, REVIEWS_LIMIT)?.map(r => (
                        <div key={r.uuid} className={styles.reviewCard}>
                            <div className={styles.cardHeader}>
                                <img
                                    className={styles.cardImage}
                                    src={r.image || DEFAULT_PROFILE_PIC_SRC}
                                    alt=""
                                    loading="lazy"
                                />
                                <div className={styles.cardTitleText}>
                                    <div className={styles.cardTitle}>
                                        {r.name}
                                    </div>
                                    <div className={styles.cardSubtitle}>
                                        {r.sub_heading}
                                    </div>
                                    {r.rating > 0 && (
                                        <Rating
                                            placeholderRating={r.rating}
                                            readonly
                                            emptySymbol={
                                                <AiOutlineStar
                                                    className={`${styles.emptyStarIcon} ${styles.smallStar}`}
                                                />
                                            }
                                            placeholderSymbol={
                                                <AiFillStar
                                                    className={`${styles.filledStarIcon} ${styles.smallStar}`}
                                                />
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                            <div className={styles.cardDesc}>
                                {trimText(r?.description, 300)}
                                {r?.description?.length >= 300 ? (
                                    <div
                                        onClick={handleShowReviews}
                                        className={styles.readMore}
                                    >
                                        Read More&nbsp;
                                        <OpenIcon />
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    ))}
                </div>
                {reviews.length > REVIEWS_LIMIT ? (
                    <ButtonV2
                        className={classNames(
                            commonStyles.secondaryButton,
                            "p-2"
                        )}
                        onClick={handleShowReviews}
                    >
                        Show all&nbsp;
                        {reviews.length}
                        &nbsp;reviews
                    </ButtonV2>
                ) : null}
            </div>
        </div>
        // TODO: render reviews modal here when migrated to mui
    );
};

export default Reviews;
